import React, { useState } from 'react';
import { useCustomFields } from '../../hooks/useCustomFields';
import { CustomFieldDefinition, CustomFieldType } from '../../types/customField';
import { useLanguage } from '../../contexts/LanguageContext';
import { useAuth } from '../../contexts/AuthContext';
import Modal from '../common/Modal';
import toast from 'react-hot-toast';
import { collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '../../lib/firebase';
import {
  PlusIcon,
  PencilSquareIcon,
  TrashIcon,
  XMarkIcon
} from '@heroicons/react/24/outline';

const FIELD_TYPES: { value: CustomFieldType; label: string }[] = [
  { value: 'text', label: 'Szöveg' },
  { value: 'number', label: 'Szám' },
  { value: 'date', label: 'Dátum' },
  { value: 'select', label: 'Legördülő lista' },
  { value: 'multiselect', label: 'Többszörös választás' },
  { value: 'checkbox', label: 'Jelölőnégyzet' },
  { value: 'email', label: 'Email' },
  { value: 'phone', label: 'Telefonszám' },
  { value: 'url', label: 'URL' }
];

const APPLICABLE_TO_OPTIONS = [
  { value: 'customer', label: 'Ügyfél' },
  { value: 'task', label: 'Feladat' },
  { value: 'project', label: 'Projekt' },
  { value: 'lead', label: 'Lead' },
  { value: 'vehicle', label: 'Jármű' },
  { value: 'quotation', label: 'Árajánlat' },
  { value: 'order', label: 'Megrendelés' },
  { value: 'invoice', label: 'Számla' }
];

interface CustomFieldFormData {
  name: string;
  key: string;
  type: CustomFieldType;
  required: boolean;
  options: string[];
  defaultValue: any;
  description: string;
  applicableTo: string[];
}

const CustomFieldsSettings: React.FC = () => {
  const { t } = useLanguage();
  const { user } = useAuth();
  const { customFields, loading, createCustomField, updateCustomField, deleteCustomField } = useCustomFields();
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingField, setEditingField] = useState<CustomFieldDefinition | null>(null);
  const [newOption, setNewOption] = useState('');
  const [deleteModal, setDeleteModal] = useState<{ isOpen: boolean; field: CustomFieldDefinition | null; usageCount: number }>({ isOpen: false, field: null, usageCount: 0 });

  const [formData, setFormData] = useState<CustomFieldFormData>({
    name: '',
    key: '',
    type: 'text',
    required: false,
    options: [],
    defaultValue: '',
    description: '',
    applicableTo: ['customer']
  });

  const resetForm = () => {
    setFormData({
      name: '',
      key: '',
      type: 'text',
      required: false,
      options: [],
      defaultValue: '',
      description: '',
      applicableTo: ['customer']
    });
    setEditingField(null);
    setIsFormOpen(false);
    setNewOption('');
  };

  const handleEdit = (field: CustomFieldDefinition) => {
    setEditingField(field);
    setFormData({
      name: field.name,
      key: field.key,
      type: field.type,
      required: field.required,
      options: field.options || [],
      defaultValue: field.defaultValue || '',
      description: field.description || '',
      applicableTo: field.applicableTo as string[]
    });
    setIsFormOpen(true);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      if (!formData.name.trim()) {
        throw new Error('A mező neve kötelező');
      }

      if (!formData.key.trim()) {
        throw new Error('A mező kulcsa kötelező');
      }

      if (formData.applicableTo.length === 0) {
        throw new Error('Legalább egy alkalmazási területet ki kell választani');
      }

      if ((formData.type === 'select' || formData.type === 'multiselect') && formData.options.length === 0) {
        throw new Error('Legördülő listához legalább egy opciót meg kell adni');
      }

      if (editingField) {
        await updateCustomField(editingField.id, {
          ...formData,
          applicableTo: formData.applicableTo as any
        });
        toast.success('Egyedi mező sikeresen frissítve!');
      } else {
        await createCustomField({
          ...formData,
          applicableTo: formData.applicableTo as any
        });
        toast.success('Egyedi mező sikeresen létrehozva!');
      }

      resetForm();
    } catch (error) {
      console.error('Error saving custom field:', error);
      toast.error(error instanceof Error ? error.message : 'Hiba történt a mentés során');
    }
  };

  const checkFieldUsage = async (field: CustomFieldDefinition): Promise<number> => {
    if (!user?.organizationId) return 0;

    let totalUsage = 0;

    // Ellenőrizzük minden alkalmazási területen
    for (const entityType of field.applicableTo) {
      try {
        let collectionName = '';
        
        // Collection név meghatározása
        switch (entityType) {
          case 'customer':
            collectionName = 'customers';
            break;
          case 'project':
            collectionName = 'projects';
            break;
          case 'task':
            collectionName = 'tasks';
            break;
          case 'lead':
            collectionName = 'leads';
            break;
          case 'vehicle':
            collectionName = 'vehicles';
            break;
          case 'quotation':
            collectionName = 'quotations';
            break;
          case 'order':
            collectionName = 'orders';
            break;
          case 'invoice':
            collectionName = 'invoices';
            break;
          default:
            continue;
        }

        // Lekérdezzük az entitásokat
        const q = query(
          collection(db, collectionName),
          where('organizationId', '==', user.organizationId)
        );
        
        const snapshot = await getDocs(q);
        
        // Számoljuk meg hány entitásnál van kitöltve ez a mező
        snapshot.docs.forEach(doc => {
          const data = doc.data();
          if (data.customFields && data.customFields[field.key] !== undefined && data.customFields[field.key] !== null && data.customFields[field.key] !== '') {
            totalUsage++;
          }
        });
      } catch (error) {
        console.error(`Error checking usage for ${entityType}:`, error);
      }
    }

    return totalUsage;
  };

  const handleDeleteClick = async (field: CustomFieldDefinition) => {
    // Ellenőrizzük hány entitásnál van használatban
    const usageCount = await checkFieldUsage(field);
    setDeleteModal({ isOpen: true, field, usageCount });
  };

  const handleDeleteConfirm = async () => {
    if (!deleteModal.field) return;

    try {
      await deleteCustomField(deleteModal.field.id);
      toast.success('Egyedi mező sikeresen törölve!');
      setDeleteModal({ isOpen: false, field: null, usageCount: 0 });
    } catch (error) {
      console.error('Error deleting custom field:', error);
      toast.error('Hiba történt a törlés során');
    }
  };

  const handleAddOption = () => {
    if (newOption.trim() && !formData.options.includes(newOption.trim())) {
      setFormData(prev => ({
        ...prev,
        options: [...prev.options, newOption.trim()]
      }));
      setNewOption('');
    }
  };

  const handleRemoveOption = (option: string) => {
    setFormData(prev => ({
      ...prev,
      options: prev.options.filter(o => o !== option)
    }));
  };

  const toggleApplicableTo = (value: string) => {
    setFormData(prev => ({
      ...prev,
      applicableTo: prev.applicableTo.includes(value)
        ? prev.applicableTo.filter(v => v !== value)
        : [...prev.applicableTo, value]
    }));
  };

  // Auto-generate key from name
  const handleNameChange = (name: string) => {
    setFormData(prev => {
      // Csak új mező esetén generáljuk automatikusan a kulcsot
      // Módosításnál (editingField) a kulcs nem változik
      const newKey = editingField 
        ? prev.key 
        : name.toLowerCase()
            .replace(/[áàâä]/g, 'a')
            .replace(/[éèêë]/g, 'e')
            .replace(/[íìîï]/g, 'i')
            .replace(/[óòôö]/g, 'o')
            .replace(/[úùûü]/g, 'u')
            .replace(/[ő]/g, 'o')
            .replace(/[ű]/g, 'u')
            .replace(/\s+/g, '_')
            .replace(/[^a-z0-9_]/g, '');
      
      return {
        ...prev,
        name,
        key: newKey
      };
    });
  };

  if (loading) {
    return <div className="text-center py-8">Betöltés...</div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
            Egyedi mezők
          </h2>
          <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
            Hozzon létre egyedi mezőket az ügyfelekhez és más entitásokhoz
          </p>
        </div>
        <button
          onClick={() => setIsFormOpen(true)}
          className="inline-flex items-center px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
        >
          <PlusIcon className="h-5 w-5 mr-2" />
          Új mező
        </button>
      </div>

      {/* Custom Fields List */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md overflow-hidden">
        {customFields.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-gray-500 dark:text-gray-400">
              Még nincsenek egyedi mezők. Hozzon létre egyet a fenti gombbal.
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
              <thead className="bg-gray-50 dark:bg-gray-700">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    Név
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    Kulcs
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    Típus
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    Alkalmazható
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    Kötelező
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    Műveletek
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                {customFields.map((field) => (
                  <tr key={field.id}>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900 dark:text-gray-100">
                        {field.name}
                      </div>
                      {field.description && (
                        <div className="text-xs text-gray-500 dark:text-gray-400">
                          {field.description}
                        </div>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <code className="text-xs bg-gray-100 dark:bg-gray-700 px-2 py-1 rounded">
                        {field.key}
                      </code>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-100">
                      {FIELD_TYPES.find(t => t.value === field.type)?.label || field.type}
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex flex-wrap gap-1">
                        {field.applicableTo.map(app => (
                          <span
                            key={app}
                            className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300"
                          >
                            {APPLICABLE_TO_OPTIONS.find(o => o.value === app)?.label || app}
                          </span>
                        ))}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-100">
                      {field.required ? (
                        <span className="text-red-600 dark:text-red-400">Igen</span>
                      ) : (
                        <span className="text-gray-500 dark:text-gray-400">Nem</span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <button
                        onClick={() => handleEdit(field)}
                        className="text-indigo-600 dark:text-indigo-400 hover:text-indigo-900 dark:hover:text-indigo-300 mr-3"
                      >
                        <PencilSquareIcon className="h-5 w-5" />
                      </button>
                      <button
                        onClick={() => handleDeleteClick(field)}
                        className="text-red-600 dark:text-red-400 hover:text-red-900 dark:hover:text-red-300"
                      >
                        <TrashIcon className="h-5 w-5" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Form Modal */}
      {isFormOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-center mb-6">
                <h3 className="text-xl font-bold text-gray-900 dark:text-gray-100">
                  {editingField ? 'Egyedi mező szerkesztése' : 'Új egyedi mező'}
                </h3>
                <button
                  onClick={resetForm}
                  className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                >
                  <XMarkIcon className="h-6 w-6" />
                </button>
              </div>

              <form onSubmit={handleSubmit} className="space-y-4">
                {/* Name */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Mező neve <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => handleNameChange(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                    placeholder="pl. Ügyfél prioritás"
                    required
                  />
                </div>

                {/* Key */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Kulcs (egyedi azonosító) <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={formData.key}
                    onChange={(e) => setFormData(prev => ({ ...prev, key: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 font-mono text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                    placeholder="Automatikusan generálódik a névből..."
                    required
                    disabled={!!editingField}
                  />
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                    {editingField 
                      ? '⚠️ A kulcs módosítás esetén nem változtatható meg, hogy ne vesszenek el az adatok'
                      : '✨ Automatikusan generálódik a névből. Szükség esetén manuálisan is módosítható.'}
                  </p>
                </div>

                {/* Type */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Típus <span className="text-red-500">*</span>
                  </label>
                  <select
                    value={formData.type}
                    onChange={(e) => setFormData(prev => ({ ...prev, type: e.target.value as CustomFieldType }))}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                    required
                  >
                    {FIELD_TYPES.map(type => (
                      <option key={type.value} value={type.value}>
                        {type.label}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Options for select/multiselect */}
                {(formData.type === 'select' || formData.type === 'multiselect') && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Opciók <span className="text-red-500">*</span>
                    </label>
                    <div className="flex gap-2 mb-2">
                      <input
                        type="text"
                        value={newOption}
                        onChange={(e) => setNewOption(e.target.value)}
                        onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddOption())}
                        className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                        placeholder="Új opció"
                      />
                      <button
                        type="button"
                        onClick={handleAddOption}
                        className="px-4 py-2 bg-gray-200 dark:bg-gray-600 text-gray-700 dark:text-gray-200 rounded-lg hover:bg-gray-300 dark:hover:bg-gray-500"
                      >
                        <PlusIcon className="h-5 w-5" />
                      </button>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {formData.options.map(option => (
                        <span
                          key={option}
                          className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200"
                        >
                          {option}
                          <button
                            type="button"
                            onClick={() => handleRemoveOption(option)}
                            className="ml-2 text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200"
                          >
                            <XMarkIcon className="h-4 w-4" />
                          </button>
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {/* Description */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Leírás
                  </label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                    rows={2}
                    placeholder="Rövid leírás a mezőről"
                  />
                </div>

                {/* Applicable To */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Alkalmazható <span className="text-red-500">*</span>
                  </label>
                  <div className="grid grid-cols-2 gap-2">
                    {APPLICABLE_TO_OPTIONS.map(option => (
                      <label
                        key={option.value}
                        className="flex items-center space-x-2 cursor-pointer"
                      >
                        <input
                          type="checkbox"
                          checked={formData.applicableTo.includes(option.value)}
                          onChange={() => toggleApplicableTo(option.value)}
                          className="rounded border-gray-300 dark:border-gray-600 text-primary-600 focus:ring-primary-500"
                        />
                        <span className="text-sm text-gray-700 dark:text-gray-300">
                          {option.label}
                        </span>
                      </label>
                    ))}
                  </div>
                </div>

                {/* Required */}
                <div>
                  <label className="flex items-center space-x-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.required}
                      onChange={(e) => setFormData(prev => ({ ...prev, required: e.target.checked }))}
                      className="rounded border-gray-300 dark:border-gray-600 text-primary-600 focus:ring-primary-500"
                    />
                    <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                      Kötelező mező
                    </span>
                  </label>
                </div>

                {/* Actions */}
                <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200 dark:border-gray-700">
                  <button
                    type="button"
                    onClick={resetForm}
                    className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-600"
                  >
                    Mégse
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-lg hover:bg-primary-700"
                  >
                    {editingField ? 'Frissítés' : 'Létrehozás'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Törlés megerősítés modal */}
      <Modal
        isOpen={deleteModal.isOpen}
        onClose={() => setDeleteModal({ isOpen: false, field: null, usageCount: 0 })}
        title="Egyedi mező törlése"
        size="md"
      >
        <div className="p-6">
          <div className="mb-4">
            <p className="text-gray-700 dark:text-gray-300 mb-2">
              Biztosan törölni szeretné a <strong>{deleteModal.field?.name}</strong> mezőt?
            </p>
            
            {deleteModal.usageCount > 0 ? (
              <div className="mt-4 p-4 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-700 rounded-lg">
                <div className="flex items-start">
                  <svg className="h-5 w-5 text-yellow-600 dark:text-yellow-400 mt-0.5 mr-2" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                  </svg>
                  <div>
                    <h4 className="text-sm font-medium text-yellow-800 dark:text-yellow-300">
                      Figyelmeztetés!
                    </h4>
                    <p className="text-sm text-yellow-700 dark:text-yellow-400 mt-1">
                      Ez a mező <strong>{deleteModal.usageCount} entitásnál</strong> van használatban.
                      A törlés esetén ezek az adatok véglegesen elvesznek!
                    </p>
                  </div>
                </div>
              </div>
            ) : (
              <div className="mt-4 p-4 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-700 rounded-lg">
                <div className="flex items-start">
                  <svg className="h-5 w-5 text-green-600 dark:text-green-400 mt-0.5 mr-2" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  <div>
                    <p className="text-sm text-green-700 dark:text-green-400">
                      Ez a mező nincs használatban, biztonságosan törölhető.
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>

          <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200 dark:border-gray-700">
            <button
              onClick={() => setDeleteModal({ isOpen: false, field: null, usageCount: 0 })}
              className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-600"
            >
              Mégse
            </button>
            <button
              onClick={handleDeleteConfirm}
              className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700"
            >
              Törlés
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default CustomFieldsSettings;
